// What a meeting, an issue row or a failed call SAYS: pure maps from server
// facts to catalog keys (`i18n/messages/meetings.ts`). No clock and no guess:
// a phase comes from the meeting's `status`, `recordingFlag` and
// `failureCode` only (`apps/web/CLAUDE.md`, "never guess from silence").

import { isUserErrorCode } from "@loom/shared";
import type {
  MeetingIssueRowDto,
  MeetingListItemDto,
  MeetingRunStateDto,
  MeetingStatus,
  RunStep,
} from "@loom/shared/meetings";

import type { MessageKey } from "../../i18n/index.js";

import type { MeetingFailure } from "./meetingApi";

/**
 * Statuses that do not move on their own: the page stops polling once every
 * meeting it shows sits in one. `needs_project` waits on the owner's pick, not
 * on a job, so it is final for polling too.
 */
const SETTLED: ReadonlySet<MeetingStatus> = new Set([
  "ready",
  "failed",
  "skipped",
  "no_recording",
  "needs_project",
]);

export const isSettledStatus = (status: MeetingStatus): boolean => SETTLED.has(status);

/**
 * `needs_project` with a project already set: the owner picked one and the
 * server woke the parked run, so the next processing tick moves it on. Not a
 * question any more, and not settled: the page keeps polling until the
 * status moves.
 */
export const projectSetAwaitingRun = (m: Pick<MeetingListItemDto, "status" | "projectId">): boolean =>
  m.status === "needs_project" && m.projectId !== null;

/** Settled for polling: a settled status, but never a pick still being picked up. */
export const isSettledMeeting = (m: Pick<MeetingListItemDto, "status" | "projectId">): boolean =>
  isSettledStatus(m.status) && !projectSetAwaitingRun(m);

/** Statuses at which the project may still be set (the server's own list). */
export const PROJECT_SETTABLE: ReadonlySet<MeetingStatus> = new Set([
  "scheduled",
  "in_meeting",
  "recorded",
  "no_recording",
  "transcribing",
  "needs_project",
]);

/**
 * The phase line for a meeting, or null once it is `ready` (the row then
 * shows its counts). A failure code the owner can act on wins over the
 * status: "Anmeldung nötig" is what stops a `filing` meeting.
 *
 * In a `manual` meeting the host starts recording in Meet by hand, so no
 * recording yet is the plan, not a fault: "Aufnahme noch nicht gestartet"
 * instead of "Aufnahme läuft nicht". A missing `recordingStart` (a server
 * from before the field) reads as `auto`.
 *
 * `needs_project` once a project is set reads "Geht gleich weiter": the pick
 * landed and the run is due, it only waits for the next processing tick.
 */
export const phaseKeyOf = (
  m: Pick<MeetingListItemDto, "status" | "recordingFlag" | "failureCode" | "recordingStart" | "projectId">,
): MessageKey | null => {
  if (m.failureCode === "no_token") return "meetings.phase.signIn";
  if (m.failureCode === "host_lost" || m.failureCode === "google_disconnected") {
    return "meetings.phase.reconnectGoogle";
  }
  switch (m.status) {
    case "scheduled":
    case "in_meeting":
      if (m.recordingFlag !== "not_running") return "meetings.phase.waiting";
      return m.recordingStart === "manual"
        ? "meetings.phase.notStarted"
        : "meetings.phase.notRunning";
    case "skipped":
      return "meetings.phase.skipped";
    case "no_recording":
      return "meetings.phase.noRecording";
    case "recorded":
      // Ended and recorded; Google is still writing the file. Processing
      // starts on its own once it is there (never promised sooner).
      return "meetings.phase.recordingFinishing";
    case "transcribing":
      return "meetings.phase.transcribing";
    case "needs_project":
      // A project is set: the run continues on the next tick, nothing to ask.
      return projectSetAwaitingRun(m) ? "meetings.phase.projectSet" : "meetings.phase.needsProject";
    case "triaging":
      return "meetings.phase.triaging";
    case "filing":
      return "meetings.phase.filing";
    case "failed":
      return "meetings.phase.failed";
    case "ready":
      return null;
  }
};

/** A run step's name on the page; null for `done` and `failed`. */
export const runStepKeyOf = (step: RunStep): MessageKey | null =>
  step === "done" || step === "failed" ? null : (`meetings.run.step.${step}` as MessageKey);

/** The line under the phase chip about the run, its keys and numbers. */
export interface RunLine {
  readonly key: MessageKey;
  readonly stepKey: MessageKey | null;
  readonly reasonKey: MessageKey | null;
  /** The attempt the line counts: the one running or due next, or the failed ones. */
  readonly n: number;
  readonly max: number;
}

/**
 * What the run says beside the status chip ("Transkribiert" alone told the
 * owner nothing for 15 minutes of failing attempts): the step that runs, the
 * attempt that runs or comes next with the last failure's kind, or the step
 * a failed run failed at, how many attempts failed and why ("Zeilen
 * zuordnen: Die Antwort des Modells war zu lang und wurde abgeschnitten.").
 * Server facts only (`MeetingRunStateDto`); null when there is nothing to
 * add (no run, done, waiting with no failure).
 */
export const runLineOf = (run: MeetingRunStateDto | null | undefined): RunLine | null => {
  if (run === null || run === undefined || run.step === "done") return null;
  const reasonKey = run.error === null ? null : (`meetings.run.error.${run.error}` as MessageKey);
  if (run.step === "failed") {
    if (reasonKey === null) return null;
    // A run that failed before the server kept the step names none.
    const failedStepKey = runStepKeyOf(run.failedStep ?? "failed");
    // One attempt only: a failure the run does not retry (a cut-off answer).
    const once = run.attempt === 1;
    const key: MessageKey =
      failedStepKey === null
        ? (once ? "meetings.run.failedOnceNoStep" : "meetings.run.failedNoStep")
        : (once ? "meetings.run.failedOnce" : "meetings.run.failed");
    return {
      key,
      stepKey: failedStepKey,
      reasonKey,
      n: run.attempt,
      max: run.maxAttempts,
    };
  }
  const stepKey = runStepKeyOf(run.step);
  if (stepKey === null) return null;
  if (run.attempt > 0 && reasonKey !== null) {
    return {
      key: run.running ? "meetings.run.retrying" : "meetings.run.retryNext",
      stepKey,
      reasonKey,
      n: run.attempt + 1,
      max: run.maxAttempts,
    };
  }
  return run.running
    ? { key: "meetings.run.running", stepKey, reasonKey: null, n: 1, max: run.maxAttempts }
    : null;
};

/**
 * The status chip of one issue row: how the filed issue stands now, else
 * where its filing is. Null when there is nothing to say (filed, but the
 * forge read failed).
 */
export const issueChipKeyOf = (row: MeetingIssueRowDto): MessageKey | null => {
  if (row.state === "filed") {
    return row.issueStatus === null ? null : (`meetings.issueStatus.${row.issueStatus}` as MessageKey);
  }
  if (row.state === "draft_failed") return "meetings.ticketState.draftFailed";
  // "Neu verarbeiten": waits for "Anlegen"; one already filed says so on its own line.
  if (row.state === "proposed") return (row.filedAs ?? null) === null ? "meetings.ticketState.proposed" : null;
  if (row.state === "file_failed") {
    return row.filingFailure
      ? (`meetings.filing.${row.filingFailure}` as MessageKey)
      : "meetings.filing.request_failed";
  }
  return "meetings.ticketState.filing";
};

/** Server codes a meeting write answers, mapped to one line each. */
const CODE_COPY: Readonly<Record<string, MessageKey>> = {
  not_pending: "meetings.error.changed",
  declined: "meetings.error.changed",
  not_declined: "meetings.error.changed",
  issue_not_found: "meetings.error.changed",
  processing: "meetings.error.processing",
  project_locked: "meetings.error.projectLocked",
  google_unavailable: "meetings.error.googleUnavailable",
  draft_failed: "meetings.error.draftFailed",
  already_promoted: "meetings.error.alreadyPromoted",
  // The doubt card: another answer won, or the question no longer waits.
  already_answered: "meetings.error.changed",
  not_retryable: "meetings.error.notRetryable",
  // "Neu verarbeiten" and "Anlegen".
  not_reprocessable: "meetings.error.notReprocessable",
  already_filed: "meetings.error.alreadyFiled",
  // "Ticket aktualisieren".
  changed_since_preview: "meetings.error.changed",
  update_running: "meetings.error.updateRunning",
  would_drop: "meetings.error.wouldDrop",
  // "Neu verarbeiten" while a promote or an answer's effect is written.
  busy: "meetings.error.busy",
  superseded: "meetings.error.changed",
  not_updatable: "meetings.error.notUpdatable",
  issue_moved: "meetings.error.notUpdatable",
  // An open question (refine light): the issue no longer takes answers, or a
  // "Bearbeiten" filled the field meanwhile.
  not_open: "meetings.error.changed",
  closed: "meetings.error.changed",
  // The import (`POST /api/meetings/import`).
  still_running: "meetings.error.stillRunning",
  no_recording: "meetings.error.noRecording",
  too_late: "meetings.error.tooLate",
  invalid_code: "meetings.import.pasteInvalid",
};

/** The line for a failed call: a user-error code's own catalog line, a
 *  meetings code's line, a refused project, else the generic line. */
export const failureKeyOf = (f: MeetingFailure): MessageKey => {
  const code = f.code;
  if (isUserErrorCode(code)) return `error.${code}` as MessageKey;
  if (code !== null && Object.prototype.hasOwnProperty.call(CODE_COPY, code)) {
    return CODE_COPY[code] as MessageKey;
  }
  if (f.status === 403 && code !== "cross_origin_request") return "meetings.error.forbidden";
  return "meetings.error.generic";
};

/** Where "Verfeinern" goes: the issue's refine URL, opened cold. */
export const refineHrefOf = (projectId: number, iid: number): string =>
  `/focus/projects/${projectId}/issues/${iid}`;
